import { useContext, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { Star } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { updatePersonalCharacter } from 'app/frontend/base/api/user'
import { AppContext } from 'app/frontend/base/config/context/app'
import { useTranslation, Trans } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  usePageInfo,
  useUserCharacterDetails,
} from 'app/frontend/base/config/queries'
import {
  characterClass,
  itemLevel,
  level,
  name,
  realm,
  refreshedAt,
  teams,
} from 'app/frontend/base/config/column-definitions/character'
import TransferModal from 'app/frontend/base/components/TransferModal'
import TroubleshootingModal from '../../../components/TroubleshootingModal'
import { cn } from '@/lib/utils'

interface Props {
  characterReferences: wowaudit.CharacterReference[]
  actionable: boolean
}

const CharacterTable = (props: Props) => {
  const { setCurrentCharacterRef } = useContext(AppContext)
  const queryClient = useQueryClient()
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [transferringCharacter, setTransferringCharacter] =
    useState<wowaudit.CharacterReference>()
  const [loadingMain, setLoadingMain] = useState<number>()
  const [loadingAction, setLoadingAction] = useState<{
    id: number
    action: 'inactive' | 'deleted' | 'transferred'
  }>()
  const {
    data: { user },
  } = usePageInfo()
  const { t } = useTranslation()

  const { data: characters } = useUserCharacterDetails(!props.actionable)

  const relevantCharacterRefs = useMemo(() => {
    return props.characterReferences.filter((character) =>
      user?.regions.includes(character.realm.region),
    )
  }, [props.characterReferences, user])

  const setMain = (character: wowaudit.CharacterReference) => {
    if (character.main || loadingMain) return
    setLoadingMain(character.id)

    updatePersonalCharacter(character.id, { main: true })
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: ['pageInfo'] })
        setCurrentCharacterRef(character)
      })
      .catch((err) => {
        displayErrorToast(err, t)
      })
      .then(() => setLoadingMain(undefined))
  }

  const processAction = (
    character: wowaudit.CharacterReference,
    action: 'inactive' | 'deleted',
  ) => {
    setLoadingAction({ id: character.id, action })

    updatePersonalCharacter(character.id, { gdprStatus: action })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['pageInfo'] })
      })
      .catch((err) => {
        displayErrorToast(err, t)
      })
      .then(() => setLoadingAction(undefined))
  }

  const CharacterActions = (character: wowaudit.CharacterReference) => (
    <>
      <Button
        variant='outline'
        size='sm'
        onClick={() => setTransferringCharacter(character)}
        disabled={loadingAction?.id === character.id}
      >
        transferred
      </Button>
      <Button
        variant='outline'
        size='sm'
        onClick={() => processAction(character, 'inactive')}
        loading={
          loadingAction?.id === character.id &&
          loadingAction?.action === 'inactive'
        }
        disabled={loadingAction?.id === character.id}
      >
        inactive
      </Button>
      <Button
        variant='outline'
        size='sm'
        onClick={() => processAction(character, 'deleted')}
        loading={
          loadingAction?.id === character.id &&
          loadingAction?.action === 'deleted'
        }
        disabled={loadingAction?.id === character.id}
      >
        deleted
      </Button>
    </>
  )

  const mappedCharacters = useMemo(() => {
    if (characters) {
      return characters.reduce((acc, character) => {
        acc[character.id] = character
        return acc
      }, {} as Record<string, wowaudit.Character>)
    } else {
      return {}
    }
  }, [characters])

  const mappedCharacterDetails = useMemo(() => {
    if (characters) {
      return characters.reduce((acc, character) => {
        if (character.details) {
          acc[character.id] = character.details
        }

        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>)
    } else {
      return {}
    }
  }, [characters])

  const columns: ColumnDef<wowaudit.CharacterReference>[] = useMemo(
    () => [
      {
        accessorKey: 'main',
        id: 'main',
        header: '',
        size: 0,
        cell: (info) => {
          const active =
            (info.getValue() && !loadingMain) ||
            loadingMain === info.row.original.id
          return (
            <div
              className='flex items-end justify-end -mr-4'
              onClick={() => setMain(info.row.original)}
            >
              <Tooltip delayDuration={150}>
                {info.getValue() ? <TooltipContent>Main</TooltipContent> : null}
                <TooltipTrigger>
                  <Star
                    size={18}
                    fill={active ? 'rgb(234, 179, 8)' : 'transparent'}
                    className={`cursor-pointer ${
                      active
                        ? 'text-warning-foreground'
                        : 'opacity-20 hover:text-warning-foreground hover:opacity-100'
                    }`}
                  />
                </TooltipTrigger>
              </Tooltip>
            </div>
          )
        },
      },
      name,
      characterClass,
      realm,
      level,
      itemLevel(mappedCharacterDetails, (character) => character.id),
      teams(t, mappedCharacters),
      refreshedAt(t, 'user', CharacterActions),
      {
        id: 'actionableReason',
        cell: () => {
          return (
            <span className='text-warning-foreground'>
              {t('profile.action-required-description')}
            </span>
          )
        },
      },
      {
        id: 'actions',
        align: 'right',
        cell: (info) => {
          return (
            <div className='flex gap-2 items-center justify-end'>
              <span className='whitespace-nowrap'>{t('shared.mark-as')}</span>
              <CharacterActions {...info.row.original} />
            </div>
          )
        },
      },
    ],
    [props.characterReferences, mappedCharacters, loadingAction, loadingMain],
  )

  return (
    <div className='mb-8'>
      {transferringCharacter && (
        <TransferModal
          key={transferringCharacter?.id}
          scope='character'
          entity={transferringCharacter}
          setOpen={(_value: boolean) => setTransferringCharacter(undefined)}
        />
      )}

      {helpModalOpen && (
        <TroubleshootingModal
          inProfile={true}
          setOpen={(value: boolean) => setHelpModalOpen(value)}
        />
      )}

      <DataTable
        columns={columns}
        className={cn(
          props.actionable ? 'border border-warning-foreground' : '',
        )}
        data={relevantCharacterRefs}
        includeHeader={true}
        emptyMessage={() => (
          <Trans
            i18nKey='profile.character-table.empty'
            components={{
              modal: (
                <a
                  className='text-primary cursor-pointer'
                  onClick={() => setHelpModalOpen(true)}
                />
              ),
            }}
          />
        )}
        visibilityState={
          props.actionable
            ? {
                main: false,
                itemLevel: false,
                teams: false,
                refreshedAt: false,
              }
            : {
                actionableReason: false,
                actions: false,
              }
        }
      />
    </div>
  )
}

export default CharacterTable
